import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { useLinkUsers, useQueryCouponUsers, useSearchLinkingUser } from 'hooks/coupon'
import { useStringChangeEvent } from 'hooks/form'
import { useQueryAllStores } from 'hooks/store'
import { HorizontalLine, Spacer } from 'components/Utility'
import { GreyButton, OrangeButton } from 'components/Button'
import { Caution, MainHeadline } from 'components/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { AlertCard, Card, CardInner, CautionCard, CouponCard, CouponUserCard } from 'components/Card'
import { State } from 'index'
import { deliveryCouponToAllUsers, fetchCoupon, linkUsers } from 'reducks/coupon/operations'
import { getConfirmCoupon } from 'reducks/coupon/selectors'
import { SearchForm } from 'components/Form'
import { openCautionModal } from 'reducks/modal/operations'
import startOfToday from 'date-fns/startOfToday'

const CouponLinkTemplate: FC = () => {
  const dispatch = useDispatch()
  const pathname = useSelector((state: State) => state.router.location.pathname)
  const couponId = /^\/admin\/coupon\/list\/.+$/.test(pathname) ? pathname.split('/admin/coupon/list/')[1] : ''
  const coupon = useSelector((state: State) => getConfirmCoupon(state))

  const [keyword, setKeyword] = useState<string>('')
  const handleKeyword = useStringChangeEvent(setKeyword)

  const backToCouponListPage = useCallback(() => dispatch(push('/admin/coupon/list')), [dispatch])
  const openDeliveryToAllModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '配布する',
        'キャンセル',
        '配布したクーポンは元に戻すことができません。本当によろしいですか？',
        () => dispatch(deliveryCouponToAllUsers(coupon)),
        '全ユーザーに配布します。'
      )
    )
  }, [coupon])

  const { isMatched, matchedUsers, searchUser } = useSearchLinkingUser(keyword)
  const { linkedUsers, addLinkedUser } = useLinkUsers()

  const stores = useQueryAllStores()
  const couponUsers = useQueryCouponUsers(couponId, 10)

  useEffect(() => {
    if (!couponId) return
    dispatch(fetchCoupon(couponId))
  }, [couponId])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'クーポンを配布します'} />
      <Spacer size={20} />
      <Spacer size={20} />
      <AlertCard
        headline={'一度配布したクーポンは取り消せないため、ユーザーIDは注意して配布してください。'}
        title={'注意'}
      />
      <Spacer size={20} />
      <CouponCard
        createdDate={coupon.created_at?.toDate() || startOfToday()}
        description={coupon.description}
        name={coupon.name}
        discountAmount={coupon.discount_amount}
        discountRate={coupon.discount_rate}
        maxDiscount={coupon.limitations.max_discount || 0}
        maxPerUser={coupon.limitations.max_per_user || 0}
        minAmount={coupon.limitations.min_amount || 0}
        expiresAt={coupon.expires_at || undefined}
        expiresMaxage={coupon.expires_maxage || 0}
        isFirst={coupon.limitations.is_first}
        storeIds={coupon.targets?.stores || []}
        stores={stores}
      />
      <Spacer size={20} />
      <MainHeadline headline={'ユーザー'} />
      <Spacer size={20} />
      {couponUsers.length === 0 && linkedUsers.length === 0 ? (
        <CautionCard caution={'配布しているユーザーは存在しません'} size={'small'} />
      ) : (
        <Card>
          <CardInner>
            {couponUsers.map((couponUser, index) => {
              return (
                <React.Fragment key={couponUser.user_id}>
                  <CouponUserCard status={couponUser.status} uid={couponUser.user_id} />
                  {(index !== couponUsers.length - 1 || linkedUsers.length > 0) && <Spacer size={20} />}
                </React.Fragment>
              )
            })}
            {couponUsers.length === 10 && (
              <>
                <Spacer size={20} />
                <Caution
                  caution={
                    'ユーザー数が多いため表示を制限しています。対象のユーザーに配布されているかは検索して確認してください。'
                  }
                />
                <Spacer size={12} />
                <HorizontalLine color={'grey04'} verticalMargin={0} />
              </>
            )}
            {linkedUsers.map((linkedUser, index) => {
              return (
                <React.Fragment key={linkedUser.user_id}>
                  <CouponUserCard status={'linking'} uid={linkedUser.user_id || ''} />
                  {index !== linkedUsers.length - 1 && <Spacer size={20} />}
                </React.Fragment>
              )
            })}
          </CardInner>
        </Card>
      )}
      <Spacer size={20} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={searchUser}
        placeholder={'ユーザー名、電話番号、ユーザーIDで検索'}
        value={keyword}
      />
      <Spacer size={20} />
      {matchedUsers.length > 0 ? (
        <Card>
          <CardInner>
            {matchedUsers.map((matchedUser, index) => {
              const link = () => {
                addLinkedUser(matchedUser)
                setKeyword('')
              }
              const isAdded =
                linkedUsers.some((linkedUser) => linkedUser.user_id === matchedUser.user_id) ||
                couponUsers.some((couponUser) => couponUser.user_id === matchedUser.user_id)
              return (
                <React.Fragment key={matchedUser.user_id}>
                  <CouponUserCard
                    addLinkedUser={link}
                    couponId={coupon.coupon_id}
                    couponUsers={couponUsers}
                    isAdded={isAdded}
                    uid={matchedUser.user_id || ''}
                  />
                  {index !== matchedUsers.length - 1 && <Spacer size={20} />}
                </React.Fragment>
              )
            })}
          </CardInner>
        </Card>
      ) : isMatched ? (
        <></>
      ) : (
        <>
          <CautionCard caution={'ユーザーが存在しません'} size={'small'} />
          <Spacer size={20} />
        </>
      )}
      <Spacer size={20} />
      <OrangeButton
        disabled={linkedUsers.length === 0}
        label={'クーポンを配布する'}
        onClick={() => dispatch(linkUsers(couponId, coupon, linkedUsers))}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={20} />
      <OrangeButton label={'全ユーザーへ配布する'} onClick={openDeliveryToAllModal} size={'medium'} type={'main'} />
      <Spacer size={20} />
      <GreyButton label={'クーポン画面へ戻る'} onClick={backToCouponListPage} size={'medium'} type={'outlined'} />
      <Spacer size={72} />
    </div>
  )
}

export default CouponLinkTemplate
